import { LoadingOutlined } from "@ant-design/icons";
import type React from "react";
import { useState } from "react";
import type { TransferClickSource } from "../../../types/index.type";
import { EventType, trackEvent } from "../../../utils/analytics";
import { useOptionalTransfer } from "../../TransferButton/TransferContext";
import { optionLabel, transferClickProperties } from "../../TransferButton/transferTarget";
import EncounterField, { useCardEncounter } from "./EncounterField";
import {
  COVERAGE_BADGES,
  type CoverageSummary,
  type RowCoverage,
  costLabel,
  selectedCostTotal,
  summaryCaption,
  summaryParts,
  totalLabel,
} from "./coverageModel";

// Post-review suggestion card: orders extracted from the just-generated note,
// offered for one-tap placement. Confirm dispatches `place_suggested_orders`
// with the SELECTED orders verbatim — the backend runs the workflow directly
// (no chat-LLM round trip) and narrates progress into the thread.
//
// When the note hasn't been transferred yet (note_transferred=false), the
// primary action is "Transfer note & place orders": the existing transfer
// flow starts (banner / appointment / MFA modals unchanged), then
// `queue_orders_after_transfer` stores the orders on the job — the backend
// places them once the note lands, on the same encounter, even if the
// clinician navigates away.
//
// Corrections happen in chat ("drop the lipid panel"): the backend re-emits
// this widget with the revised list and the older card renders FROZEN — the
// same list, greyed and non-interactive, with a caption saying what became of
// it. A card the clinician just acted on freezes the same way, from local
// `phase` alone: nothing that happened in the thread disappears, including the
// card you acted on, because the list IS the record of what was ordered.
// Extra fields (dx, med sig, cpt code…) ride along untouched — the card's
// payload goes to the workflow verbatim, so this type stays open.
export interface SuggestedOrder {
  test_name: string;
  order_type?: string;
  search_aliases?: string[];
  dx?: string;
  strength?: string;
  formulation?: string;
  take?: string;
  freq?: string;
  duration?: string;
  dispense?: string;
  refills?: string;
  code?: string;
  urgency?: string;
  fasting?: boolean;
  // An order to be done later: filed in the EHR as a future order for this
  // date (YYYY-MM-DD). Shown on the row with a date field the clinician can
  // change before placing; the edited date travels in the place payload.
  future_order_date?: string;
  // Coding-engine billing codes (kind: visit_code | procedure_code): the
  // exact code + modifiers shown so checking the row IS the billing
  // confirmation, with the engine's rationale under it.
  kind?: string;
  modifiers?: string[];
  rationale?: string;
  preconfirmed?: boolean;
  // The same order was placed for this patient in the last 24h. Badged and
  // (with `deselected`) unchecked by default — re-checking it IS the
  // clinician confirming the repeat, so the row goes out with
  // allow_duplicate.
  duplicate_recent?: boolean;
  deselected?: boolean;
  allow_duplicate?: boolean;
  // Superdoc memory. `memory` is server-derived card state: the remembered
  // catalog item this order goes to without a pick card (mode "auto"), or —
  // when the clinician asked by chat for a different one — that the list
  // will be shown once at placement ("ask": this time; "relearn": and the
  // pick becomes the new usual). `pick_mode` is the clinician's ask itself
  // and travels with the order; `memory` never leaves the card.
  // `memory.default_unchecked`: memory started the row unchecked; a row may
  // carry only this, with no remembered pick.
  pick_mode?: "ask" | "relearn";
  memory?: {
    entry_id?: string;
    label?: string;
    mode?: "auto" | "ask" | "relearn";
    default_unchecked?: { entry_id: string; layer: string };
  };
  // EHR catalogue resolution, done up front over the warm EHR session so the
  // card shows what each order BECOMES ("CBC → CBC With Differential/Platelet")
  // and any pick happens here, in one click, instead of on a second card
  // after the transfer. `catalog_item_id` is the confirmed selection the
  // backend places without pausing; `catalog_options` is the ranked list to
  // choose from when the EHR offered several; `catalog_state` is the
  // resolver's verdict (resolved | ambiguous | not_found | …). Absent on a
  // card built without a warm session — the row then renders as before and
  // the placement run asks if it must.
  catalog_item_id?: string;
  catalog_label?: string;
  catalog_options?: CatalogOption[];
  catalog_state?: string;
  // Why the EHR connector can't place this row (catalog_state "invalid").
  catalog_detail?: string;
  // The diagnosis this order files under, settled up front against the
  // note's own diagnostic codes (the list the transfer writes onto the
  // encounter). `dx_code` is the confirmed ICD code (or "other"); `dx_options`
  // is the note's diagnoses plus the explicit Other when the note didn't make
  // it clear; `dx_state` is resolved | ambiguous. Absent on a note with no
  // codes — the row renders as before and the placement run asks if it must.
  dx_code?: string;
  dx_label?: string;
  dx_options?: CatalogOption[];
  dx_state?: string;
  // Coverage-aware card (POC): covered / prior auth / not covered / unknown
  // plus a patient-cost estimate for lab and imaging rows. Server-derived,
  // card-only, advisory — never changes what is placed and never unchecks
  // a row (``coverage_card.service``).
  coverage?: RowCoverage;
}

export interface CatalogOption {
  value: string;
  label: string;
}

// The options a row's select shows: every one the EHR offered, ranked, the
// probable ones first. A folded shortlist with a "Show more" button was
// tried and dropped: it added the rest to the same native select and
// vanished, so the click looked like it did nothing.
export const visibleCatalogOptions = (options: CatalogOption[] | undefined): CatalogOption[] => options ?? [];

export const memoryUnchecked = (o: SuggestedOrder): boolean => !!o.memory?.default_unchecked;

// Count-only kept-vs-changed for the card's analytics event, rolled up from
// the per-row outcomes the backend records (`order_cards.row_outcomes`).
// A pick counts only on a sent row that offered a dropdown (no settled
// `catalog_item_id` / `dx_code`); its rank is its 1-based position in the
// card's own option order. `dx_changed` is a diagnosis pick that was not the
// first option. `rows_memory_unchecked` counts rows memory started
// unchecked. No names, codes or patient data — counts only.
export const orderCardOutcomeCounts = (
  orders: SuggestedOrder[],
  sent: Set<number>,
  picks: Record<number, string>,
  dxPicks: Record<number, string>,
) => {
  const rank = (options: CatalogOption[] | undefined, value: string | undefined) =>
    value ? (options ?? []).findIndex((o) => o.value === value) + 1 : 0;
  const counts = {
    rows_offered: orders.length,
    rows_sent: 0,
    rows_unchecked: 0,
    catalog_picks: 0,
    catalog_picks_not_top: 0,
    dx_picks: 0,
    dx_changed: 0,
    rows_memory_unchecked: 0,
  };
  orders.forEach((o, i) => {
    if (memoryUnchecked(o)) counts.rows_memory_unchecked += 1;
    if (!sent.has(i)) return;
    counts.rows_sent += 1;
    const catalogRank = o.catalog_item_id ? 0 : rank(o.catalog_options, picks[i]);
    if (catalogRank > 0) counts.catalog_picks += 1;
    if (catalogRank > 1) counts.catalog_picks_not_top += 1;
    const dxRank = o.dx_code ? 0 : rank(o.dx_options, dxPicks[i]);
    if (dxRank > 0) counts.dx_picks += 1;
    if (dxRank > 1) counts.dx_changed += 1;
  });
  counts.rows_unchecked = counts.rows_offered - counts.rows_sent;
  return counts;
};

// What the card says under an order about its remembered pick.
export const memoryDetail = (o: SuggestedOrder): string => {
  if (!o.memory?.entry_id) return "";
  // With the catalogue resolved on the card, the pick happens HERE: the
  // dropdown under the row carries the remembered item first.
  if (o.memory.mode === "ask")
    return o.catalog_options?.length ? "→ your pick — choose below" : "→ you'll pick this one at placement";
  if (o.memory.mode === "relearn")
    return o.catalog_options?.length
      ? "→ your pick — choose below and I'll remember it"
      : "→ you'll pick at placement and I'll remember it";
  // A remembered item the card already shows as the row's resolution is
  // labelled on that line ("· your usual"), not repeated here.
  if (o.catalog_item_id && o.catalog_label) return "";
  return `→ ${o.memory.label} · your usual`;
};

// "2026-12-15" → "Dec 15, 2026" for the frozen row; anything else as given.
export const futureDateLabel = (iso?: string): string => {
  const v = (iso || "").trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return v;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// Today as YYYY-MM-DD in the browser's zone — the earliest date the field accepts.
const todayIso = (): string => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// A medication the note says to STOP. Display-only: nothing in the EHR
// workflow discontinues a med, so the card must not pretend it can.
export interface OrderStop {
  name: string;
  reason?: string;
}

// "disp #45" / "disp 30 tablets" — bare numbers get the pharmacy `#`, an
// already-qualified quantity is shown as written.
const dispenseLabel = (dispense?: string): string => {
  const v = (dispense || "").trim();
  if (!v) return "";
  if (v.startsWith("#")) return `disp ${v}`;
  return /^\d+(\.\d+)?$/.test(v) ? `disp #${v}` : `disp ${v}`;
};

// The sig line shown under a medication so the clinician sees exactly what
// will be placed ("500 mg — 1 tablet · Twice a day · 90 days · disp #60 · 2 refills").
const medDetail = (o: SuggestedOrder): string => {
  const sig = [o.take, o.freq, o.duration, dispenseLabel(o.dispense), o.refills ? `${o.refills} refills` : ""]
    .filter(Boolean)
    .join(" · ");
  return [o.strength, sig].filter(Boolean).join(" — ");
};

// The billed code line under a coding suggestion ("99213-25" / "G0439") — the
// clinician must see the exact code they're confirming, modifiers included.
const cptDetail = (o: SuggestedOrder): string => {
  const code = (o.code || "").trim();
  if (!code) return "";
  return [code, ...(o.modifiers || [])].join("-");
};

export interface OrderSuggestionsData {
  audio_file_id?: number | null;
  // Stable id for this card across revisions (new backend); absent on cards
  // persisted before the staged-orders work.
  card_id?: string;
  ehr_label?: string;
  question?: string;
  orders?: SuggestedOrder[];
  stops?: OrderStop[];
  unparsed?: string[];
  // Coding rows that are only a CPT modifier ("95") — left off the card.
  skipped_codes?: string[];
  // Orders the note stated that a memory rule left off this card (a practice
  // that places only its billing codes from Nudge): named so the clinician
  // can see what the note said and the card did not offer.
  omitted?: { test_name: string; order_type?: string }[];
  blocked_reason?: string | null;
  note_transferred?: boolean;
  // Coverage-aware card (POC): payer, plan status, deductible progress for
  // the header strip. Absent when the practice isn't flagged in.
  coverage_summary?: CoverageSummary | null;
  // "chat" cards are staged from dictation/chat with no note behind them —
  // there is nothing to transfer, so the combined action doesn't apply.
  source?: string;
  // Lifecycle, set by the backend on restore ("active" | "superseded" |
  // "placed" | "dismissed") plus a short human caption. Live-streamed cards
  // carry neither: the newest card in the thread is the active one.
  status?: string;
  resolution?: string | null;
  // Row indexes the clinician actually sent, written when the card was
  // resolved. A reloaded frozen card checks only these, so it shows what
  // went to the EHR instead of every row ticked.
  sent_indexes?: number[];
}

interface OrderSuggestionsProps {
  data: OrderSuggestionsData;
  onCustomAction: (action: { type: string; payload?: Record<string, unknown> }) => void;
  disabled?: boolean;
  // This card is history: a newer card superseded it, or it was already
  // placed/dismissed. Renders in full but greyed and inert — the thread must
  // still show what was offered and what happened to it.
  frozen?: boolean;
}

type Phase = "input" | "submitted" | "combined_submitted" | "dismissed";

const ORDER_TYPE_BADGES: Record<string, { label: string; color: string; bg: string }> = {
  lab: { label: "Lab", color: "#0E7490", bg: "#ECFEFF" },
  di: { label: "Imaging", color: "#6D28D9", bg: "#F5F3FF" },
  procedure: { label: "Procedure", color: "#B45309", bg: "#FFFBEB" },
  medication: { label: "Medication", color: "#BE185D", bg: "#FDF2F8" },
  cpt: { label: "Visit code", color: "#166534", bg: "#F0FDF4" },
};

// Coding-engine rows refine the generic cpt badge by kind.
const KIND_BADGES: Record<string, { label: string; color: string; bg: string }> = {
  visit_code: ORDER_TYPE_BADGES.cpt,
  procedure_code: { label: "Procedure code", color: "#3730A3", bg: "#EEF2FF" },
};

const badgeFor = (o: SuggestedOrder) =>
  (o.kind && KIND_BADGES[o.kind]) || (o.order_type ? ORDER_TYPE_BADGES[o.order_type] : undefined);

// What a frozen card says happened to it. The backend's `resolution` wins;
// the status map covers cards persisted without one, and the last fallback is
// the legacy last-wins case (no status at all).
const STATUS_CAPTIONS: Record<string, string> = {
  placed: "Placed",
  dismissed: "Dismissed — nothing was ordered.",
  superseded: "Updated — see the newer list below.",
};

const frozenCaption = (data: OrderSuggestionsData): string =>
  (data.resolution || "").trim() || STATUS_CAPTIONS[data.status || ""] || "Updated — see the newer list below.";

const BoltIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, display: "block" }}>
    <path
      d="M8.5 1.5 3 9h4l-.5 5.5L13 7H8.5l0-5.5Z"
      fill="#EEF2FF"
      stroke="#4F46E5"
      strokeWidth="1.3"
      strokeLinejoin="round"
    />
  </svg>
);

// "Today 3:00 PM · Office Visit" — the read-only field after a click.
const encounterSummary = (option: {
  day_label?: string;
  time?: string;
  time_label?: string;
  date?: string;
  visit_type?: string;
}) => [optionLabel(option), option.visit_type].filter(Boolean).join(" · ");

const OrderSuggestions: React.FC<OrderSuggestionsProps> = ({ data, onCustomAction, disabled, frozen }) => {
  const orders = data?.orders ?? [];
  const stops = data?.stops ?? [];
  const unparsed = (data?.unparsed ?? []).filter(Boolean);
  const skippedCodes = (data?.skipped_codes ?? []).filter(Boolean);
  const omitted = (data?.omitted ?? []).map((o) => (o?.test_name || "").trim()).filter(Boolean);
  const transfer = useOptionalTransfer();
  const [phase, setPhase] = useState<Phase>("input");
  const [isStartingTransfer, setIsStartingTransfer] = useState(false);
  // Checked by default — the card exists because the note says to order these.
  // Duplicates the backend flagged (`deselected`) start unchecked: repeating
  // an order placed hours ago has to be a deliberate re-check, as do rows
  // memory left unchecked. Keyed by index, not name: two same-named orders
  // must not collide.
  const [selected, setSelected] = useState<Set<number>>(() =>
    Array.isArray(data?.sent_indexes)
      ? new Set(data.sent_indexes.filter((i) => Number.isInteger(i) && i >= 0 && i < orders.length))
      : new Set(orders.map((_, i) => i).filter((i) => !orders[i]?.deselected)),
  );
  // Future-order dates as edited on the card, keyed by index. Only rows the
  // backend marked as future orders get a field; an empty pick snaps back to
  // the previous date so a slip can never silently turn it into a today order.
  const [dates, setDates] = useState<Record<number, string>>(() =>
    Object.fromEntries(orders.map((o, i) => [i, (o.future_order_date || "").trim()]).filter(([, v]) => !!v)),
  );
  // The clinician's inline catalogue picks, by row. Nothing is pre-selected:
  // a real order one mis-tap away is exactly what the pick card avoided.
  const [picks, setPicks] = useState<Record<number, string>>({});
  // The clinician's inline diagnosis picks, by row — same rule, nothing
  // pre-selected.
  const [dxPicks, setDxPicks] = useState<Record<number, string>>({});
  // The Encounter field (one-click EHRs): only on a note's own card whose note
  // is not in the EHR yet — once it is, orders reuse the note's encounter.
  const field = useCardEncounter(
    !frozen && !data?.note_transferred && data?.source !== "chat" ? data?.audio_file_id : null,
  );
  // What the field held when an action was clicked: shown read-only after.
  const [lockedEncounter, setLockedEncounter] = useState("");

  if (!orders.length && !stops.length) return null;

  const blockedMissingMrn = data.blocked_reason === "missing_mrn";
  const noteTransferred = !!data.note_transferred;
  // A chat-staged card has no note behind it (audio_file_id null), so
  // "Transfer note & place orders" would promise a transfer that can't happen.
  const canCombine = !noteTransferred && !!transfer && !!data.audio_file_id && data.source !== "chat";

  const toggle = (index: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  // A duplicate the clinician re-checked is a confirmed repeat — stamp
  // allow_duplicate so the backend's 24h gate doesn't silently drop it. An
  // inline catalogue pick rides out as `catalog_item_id`, the same field a
  // pre-resolved row already carries; the backend checks every pick against
  // the card it persisted before placing it.
  const selectedOrders = () =>
    orders
      .map((o, i) => (dates[i] ? { ...o, future_order_date: dates[i] } : o))
      .map((o, i) => (picks[i] ? { ...o, catalog_item_id: picks[i] } : o))
      .map((o, i) => (dxPicks[i] ? { ...o, dx_code: dxPicks[i] } : o))
      .filter((_, i) => selected.has(i))
      .map((o) => (o.duplicate_recent ? { ...o, allow_duplicate: true } : o));

  const setDate = (index: number, value: string) => {
    const v = (value || "").trim();
    if (!v) return;
    setDates((prev) => ({ ...prev, [index]: v }));
  };

  // Checked rows the EHR offered several items for that still have no pick.
  // Placing one of those bare would re-create the second card after the
  // transfer — the exact pause this card exists to remove — so the actions
  // wait until every checked ambiguous row has a choice.
  const needsCatalogPick = (o: SuggestedOrder, i: number) =>
    !o.catalog_item_id && (o.catalog_options?.length ?? 0) > 0 && !picks[i];
  const needsDxPick = (o: SuggestedOrder, i: number) => !o.dx_code && (o.dx_options?.length ?? 0) > 0 && !dxPicks[i];
  const pendingPicks = orders.filter((o, i) => selected.has(i) && (needsCatalogPick(o, i) || needsDxPick(o, i))).length;

  // The field's appointment rides on both actions; without the field (other
  // EHRs, schedule not read) nothing is sent and today's behaviour holds.
  const chosenAppointment = field.active ? field.option : null;
  const fieldTarget = field.entry.target;
  const clickSource: TransferClickSource = !chosenAppointment
    ? "legacy"
    : fieldTarget?.selected_id === chosenAppointment.id
      ? "chosen"
      : "suggested";
  // Which action answered the card, and whether its Encounter field was
  // left on the suggestion — flags and counts only.
  const trackCardAction = (action: "transfer_and_place" | "place_only" | "dismiss") =>
    trackEvent(EventType.SUPERDOC_ORDER_CARD_ACTION, {
      action,
      field_active: field.active,
      order_count: selected.size,
      // A dismiss sends nothing: every offered row counts as unchecked.
      ...orderCardOutcomeCounts(orders, action === "dismiss" ? new Set() : selected, picks, dxPicks),
      source: clickSource,
      ...transferClickProperties(field.active ? fieldTarget : null, chosenAppointment?.id ?? null),
    });

  const place = () => {
    if (disabled || blockedMissingMrn || !selected.size || pendingPicks || field.needsChoice) return;
    onCustomAction({
      type: "place_suggested_orders",
      payload: {
        audio_file_id: data.audio_file_id,
        ehr_label: data.ehr_label,
        orders: selectedOrders(),
        ...(chosenAppointment ? { appointment_id: chosenAppointment.id } : {}),
      },
    });
    trackCardAction("place_only");
    if (chosenAppointment) setLockedEncounter(encounterSummary(chosenAppointment));
    setPhase("submitted");
  };

  const transferAndPlace = async () => {
    if (
      disabled ||
      blockedMissingMrn ||
      !selected.size ||
      pendingPicks ||
      !transfer ||
      !data.audio_file_id ||
      isStartingTransfer ||
      field.needsChoice
    )
      return;
    setIsStartingTransfer(true);
    trackCardAction("transfer_and_place");
    trackEvent(EventType.TRANSFER_ONE_CLICK, {
      source: clickSource,
      surface: "superdoc_card",
      ...transferClickProperties(field.active ? fieldTarget : null, chosenAppointment?.id ?? null),
    });
    try {
      const result = await transfer.startTransfer(data.audio_file_id, chosenAppointment?.id, {
        surface: "superdoc_card",
        source: clickSource,
        suggested_id: field.active ? fieldTarget?.suggested_id ?? null : null,
      });
      if (result.jobId) {
        // A job id comes back for a fresh transfer, one already in flight
        // ("in_progress"), AND an already-completed one
        // ("already_transferred") — the backend chains the orders behind
        // that job, placing immediately when it's already done.
        onCustomAction({
          type: "queue_orders_after_transfer",
          payload: {
            audio_file_id: data.audio_file_id,
            transfer_job_id: result.jobId,
            orders: selectedOrders(),
          },
        });
        // Don't promise a transfer that isn't happening: for a completed
        // job the orders are being placed right now.
        if (chosenAppointment) setLockedEncounter(encounterSummary(chosenAppointment));
        setPhase(result.code === "already_transferred" ? "submitted" : "combined_submitted");
      }
      // No job id means the transfer couldn't start; startTransfer already
      // surfaced the error toast and the card stays interactive for a retry.
    } finally {
      setIsStartingTransfer(false);
    }
  };

  const dismiss = () => {
    if (disabled) return;
    onCustomAction({ type: "order_suggestions_dismiss" });
    trackCardAction("dismiss");
    setPhase("dismissed");
  };

  const busy = disabled || isStartingTransfer;
  // Acting on the card freezes it in place — the full list stays on screen,
  // greyed and inert, so the clinician can refer back to what was sent.
  const inert = !!frozen || phase !== "input";
  // A card the clinician acted on THIS session: the local outcome is truer
  // than anything persisted on the payload (which a poll may not have written
  // yet, or which a newer card froze under a different resolution).
  const localCaption =
    phase === "submitted"
      ? "Sent to your EHR — I'll keep you posted in the chat."
      : phase === "combined_submitted"
        ? "Transferring the note first, then placing these — you can move on."
        : phase === "dismissed"
          ? "Dismissed — nothing was ordered."
          : "";
  const count = selected.size;
  // Live count on the primary action ("place 2 orders") so partial selections
  // read back exactly what will happen; no count while nothing is selected
  // (the button is disabled then anyway). Billing codes aren't "orders" — a
  // codes-only selection says "codes", a mix says "items".
  const pickedCodes = selectedOrders().filter((o) => o.kind).length;
  const noun = pickedCodes === 0 ? "order" : pickedCodes === count ? "code" : "item";
  const countedOrders = count ? `${count} ${noun}${count === 1 ? "" : "s"}` : "orders";
  const primaryDisabled = busy || blockedMissingMrn || !count || pendingPicks > 0 || field.needsChoice;
  const pickHint =
    field.needsChoice && field.hint
      ? field.hint
      : pendingPicks > 0
        ? `${pendingPicks} ${pendingPicks === 1 ? "order" : "orders"} above still ${pendingPicks === 1 ? "needs" : "need"} a choice to continue.`
        : null;

  const coverageParts = summaryParts(data.coverage_summary);
  const coverageCaption = summaryCaption(data.coverage_summary);
  const coverageTotal = totalLabel(selectedCostTotal(orders, selected));

  return (
    <div style={{ ...styles.container, ...(inert ? styles.frozenBox : {}) }}>
      <div style={styles.header}>
        <div style={{ ...styles.iconBadge, ...(inert ? styles.iconBadgeFrozen : {}) }}>
          <BoltIcon />
        </div>
        <span style={{ ...styles.headerText, ...(inert ? styles.frozenText : {}) }}>
          {data.question || `This note's plan includes orders I can place in ${data.ehr_label || "your EHR"}:`}
        </span>
      </div>

      {coverageParts.length > 0 && (
        <div style={{ ...styles.coverageStrip, ...(inert ? styles.frozenText : {}) }} data-testid="coverage-strip">
          <span style={styles.coverageStripMain}>{coverageParts.join(" · ")}</span>
          {coverageCaption && <span style={styles.coverageStripCaption}>{coverageCaption}</span>}
        </div>
      )}

      {blockedMissingMrn && !inert && (
        <div style={styles.blockedBanner}>
          Add this patient's {data.ehr_label || "eClinicalWorks"} account number to their chart to place orders.
        </div>
      )}

      {orders.length > 0 && (
        <div style={{ ...styles.orderList, ...(inert ? styles.orderListFrozen : {}) }}>
          {orders.map((o, i) => {
            const badge = badgeFor(o);
            const checked = selected.has(i);
            const detail = o.order_type === "medication" ? medDetail(o) : o.order_type === "cpt" ? cptDetail(o) : "";
            const memoryLine = memoryDetail(o);
            const futureDate = dates[i] || "";
            const rationale = o.order_type === "cpt" ? (o.rationale || "").trim() : "";
            // Frozen cards grey as a whole (box + list); rows that were left
            // unchecked still fade so a placed card reads back accurately.
            const dimmed = !checked;
            return (
              <label
                key={`${o.test_name}#${i}`}
                style={{
                  ...styles.orderRow,
                  ...(i > 0 ? styles.orderRowDivider : {}),
                  ...(busy || inert ? styles.orderRowBusy : {}),
                }}
              >
                <input
                  type="checkbox"
                  disabled={busy || inert}
                  checked={checked}
                  onChange={() => !inert && toggle(i)}
                  style={styles.checkbox}
                />
                <span style={{ ...styles.orderTextCol }}>
                  <span style={styles.orderNameRow}>
                    <span style={{ ...styles.orderName, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      {o.test_name}
                    </span>
                    {o.duplicate_recent && <span style={styles.duplicateBadge}>placed in last 24h</span>}
                  </span>
                  {detail && (
                    <span style={{ ...styles.orderDetail, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      {detail}
                    </span>
                  )}
                  {memoryLine && (
                    <span style={{ ...styles.orderMemory, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      {memoryLine}
                    </span>
                  )}
                  {memoryUnchecked(o) && (
                    <span style={{ ...styles.orderMemory, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      Usually left unchecked
                    </span>
                  )}
                  {futureDate &&
                    (inert ? (
                      <span style={{ ...styles.orderFuture, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                        Future order · {futureDateLabel(futureDate)}
                      </span>
                    ) : (
                      // The input is interactive content, so a click on it
                      // never reaches the row's <label> and the checkbox stays.
                      <span style={{ ...styles.orderFuture, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                        Future order ·{" "}
                        <input
                          type="date"
                          aria-label={`Future order date for ${o.test_name}`}
                          value={futureDate}
                          min={todayIso()}
                          disabled={busy}
                          onChange={(e) => setDate(i, e.target.value)}
                          style={styles.dateInput}
                        />
                      </span>
                    ))}
                  {rationale && (
                    <span
                      style={{ ...styles.orderRationale, ...(dimmed ? styles.orderNameUnchecked : {}) }}
                      title={rationale}
                    >
                      {rationale}
                    </span>
                  )}
                  {o.catalog_item_id && o.catalog_label && (
                    <span style={{ ...styles.catalogLine, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      <span style={styles.catalogArrow}>→</span>
                      {o.catalog_label}
                      {o.memory?.mode === "auto" ? " · your usual" : ""}
                    </span>
                  )}
                  {!o.catalog_item_id && (o.catalog_options?.length ?? 0) > 0 && (
                    <span style={styles.catalogPickRow}>
                      <span style={styles.catalogArrow}>→</span>
                      <select
                        value={picks[i] ?? ""}
                        disabled={busy || inert || !checked}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPicks((prev) => {
                            const next = { ...prev };
                            if (value) {
                              next[i] = value;
                            } else {
                              delete next[i];
                            }
                            return next;
                          });
                        }}
                        aria-label={`${data.ehr_label || "EHR"} item for ${o.test_name}`}
                        style={{ ...styles.catalogSelect, ...(picks[i] ? {} : styles.catalogSelectEmpty) }}
                      >
                        <option value="">Choose the {data.ehr_label || "EHR"} item…</option>
                        {visibleCatalogOptions(o.catalog_options).map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  )}
                  {!o.catalog_item_id && !o.catalog_options?.length && o.catalog_state === "not_found" && (
                    <span style={{ ...styles.catalogMiss, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      Not found in {data.ehr_label || "the EHR"} by this name — I'll search again when placing and ask
                      if needed.
                    </span>
                  )}
                  {o.catalog_state === "invalid" && (
                    <span style={{ ...styles.catalogMiss, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      Can't be placed: {o.catalog_detail || "this order isn't valid as written"}.
                    </span>
                  )}
                  {o.dx_code && (
                    <span style={{ ...styles.dxLine, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      <span style={styles.catalogArrow}>↳</span>
                      files under{" "}
                      {o.dx_code === "other" ? "Other" : `${o.dx_code}${o.dx_label ? ` · ${o.dx_label}` : ""}`}
                    </span>
                  )}
                  {!o.dx_code && (o.dx_options?.length ?? 0) > 0 && (
                    <span style={styles.catalogPickRow}>
                      <span style={styles.catalogArrow}>↳</span>
                      <select
                        value={dxPicks[i] ?? ""}
                        disabled={busy || inert || !checked}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDxPicks((prev) => {
                            const next = { ...prev };
                            if (value) {
                              next[i] = value;
                            } else {
                              delete next[i];
                            }
                            return next;
                          });
                        }}
                        aria-label={`Diagnosis for ${o.test_name}`}
                        style={{ ...styles.catalogSelect, ...(dxPicks[i] ? {} : styles.catalogSelectEmpty) }}
                      >
                        <option value="">Choose the diagnosis to file under…</option>
                        {o.dx_options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </span>
                  )}
                  {o.coverage?.reason && (
                    <span
                      style={{ ...styles.coverageDetail, ...(dimmed ? styles.orderNameUnchecked : {}) }}
                      title={o.coverage.cpt ? `CPT ${o.coverage.cpt}` : undefined}
                    >
                      {o.coverage.reason}
                      {o.coverage.covering_dx?.in_note && (
                        <>
                          {" "}
                          <span style={styles.coverageHint}>
                            Filing under {o.coverage.covering_dx.code} would make it covered.
                          </span>
                        </>
                      )}
                      {o.coverage.abn_eligible && (
                        <>
                          {" "}
                          <span style={styles.coverageHint}>An ABN is needed to bill the patient.</span>
                        </>
                      )}
                    </span>
                  )}
                </span>
                <span style={styles.rowBadgeCol}>
                  {badge && (
                    <span style={{ ...styles.typeBadge, color: badge.color, backgroundColor: badge.bg }}>
                      {badge.label}
                    </span>
                  )}
                  {o.coverage && (
                    <span
                      style={{
                        ...styles.coverageBadge,
                        color: COVERAGE_BADGES[o.coverage.state].color,
                        backgroundColor: COVERAGE_BADGES[o.coverage.state].bg,
                        borderColor: COVERAGE_BADGES[o.coverage.state].border,
                      }}
                      title={o.coverage.reason}
                    >
                      {COVERAGE_BADGES[o.coverage.state].label}
                    </span>
                  )}
                  {o.coverage && costLabel(o.coverage) && (
                    <span style={{ ...styles.coverageCost, ...(dimmed ? styles.orderNameUnchecked : {}) }}>
                      {costLabel(o.coverage)}
                      {o.coverage.cost_detail && (
                        <span style={styles.coverageCostDetail}>{o.coverage.cost_detail}</span>
                      )}
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>
      )}

      {stops.length > 0 && (
        <div style={styles.stopsBox}>
          <span style={styles.stopsTitle}>Stop — do this in your EHR, I can't discontinue meds:</span>
          {stops.map((s) => (
            <span key={s.name} style={styles.stopRow}>
              <span style={styles.stopBadge}>Stop</span>
              <span style={styles.stopText}>{[s.name, (s.reason || "").trim()].filter(Boolean).join(" — ")}</span>
            </span>
          ))}
        </div>
      )}

      {unparsed.length > 0 && (
        <div style={styles.unparsedLine}>
          I couldn't fully parse: {unparsed.join(", ")} — tell me and I'll add them.
        </div>
      )}

      {omitted.length > 0 && (
        <div style={styles.unparsedLine}>
          Also in the note, left off this card by a memory rule: {omitted.join(", ")}.
        </div>
      )}

      {skippedCodes.length > 0 && (
        <div style={styles.unparsedLine}>
          {skippedCodes.length === 1
            ? `Coding also listed ${skippedCodes[0]} — a CPT modifier, not a billing code on its own. Add it to the visit code in ${data?.ehr_label || "the EHR"} if it applies.`
            : `Coding also listed ${skippedCodes.join(", ")} — CPT modifiers, not billing codes on their own. Add them to the visit code in ${data?.ehr_label || "the EHR"} if they apply.`}
        </div>
      )}

      {inert ? (
        <>
          {lockedEncounter && data.audio_file_id ? (
            <EncounterField audioId={data.audio_file_id} field={field} lockedLabel={lockedEncounter} />
          ) : null}
          <div style={styles.captionRow}>
            {/* Only the combined action is still in flight when the card freezes. */}
            {phase === "combined_submitted" && (
              <LoadingOutlined spin style={{ color: "#9CA3AF", fontSize: 12, marginTop: 2 }} />
            )}
            <span style={styles.frozenCaption}>{localCaption || frozenCaption(data)}</span>
          </div>
        </>
      ) : (
        <>
          <div style={pickHint ? styles.pickHint : styles.hint}>
            {pickHint ?? (field.hint || "Need changes? Tell me below and I'll update this list.")}
          </div>
          {coverageTotal && (
            <div style={styles.coverageTotal} data-testid="coverage-total">
              Selected: est. patient cost <strong>{coverageTotal}</strong>
            </div>
          )}

          {field.active && data.audio_file_id ? (
            <EncounterField audioId={data.audio_file_id} field={field} disabled={busy} />
          ) : null}

          <div style={styles.actions}>
            {canCombine ? (
              <>
                <button
                  type="button"
                  disabled={primaryDisabled}
                  onClick={transferAndPlace}
                  style={{ ...styles.primaryButton, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
                >
                  {isStartingTransfer ? "Starting…" : `Transfer note & place ${countedOrders}`}
                </button>
                <div style={styles.secondaryRow}>
                  <button
                    type="button"
                    disabled={primaryDisabled}
                    onClick={place}
                    style={{ ...styles.secondaryButton, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
                  >
                    {count === 1 ? `Place ${noun} only` : `Place ${noun}s only`}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={dismiss}
                    style={{ ...styles.ghostButton, ...(busy ? styles.buttonDisabled : {}) }}
                  >
                    Not now
                  </button>
                </div>
              </>
            ) : (
              <div style={styles.secondaryRow}>
                <button
                  type="button"
                  disabled={primaryDisabled}
                  onClick={place}
                  style={{ ...styles.primaryButton, flex: 1, ...(primaryDisabled ? styles.buttonDisabled : {}) }}
                >
                  {`Place ${countedOrders}`}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={dismiss}
                  style={{ ...styles.ghostButton, ...(busy ? styles.buttonDisabled : {}) }}
                >
                  Not now
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    marginTop: "8px",
    borderRadius: "14px",
    border: "1px solid #E2E4F0",
    backgroundColor: "#FFFFFF",
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
    // Ancestors center-align text; every widget defends itself.
    textAlign: "left",
  },
  // Frozen = history: still fully readable, visibly not the live card.
  frozenBox: { backgroundColor: "#F9FAFB", border: "1px solid #E5E7EB", boxShadow: "none" },
  frozenText: { color: "#6B7280" },
  captionRow: { display: "flex", alignItems: "flex-start", gap: "7px" },
  frozenCaption: { fontSize: "12px", fontWeight: 500, color: "#9CA3AF", lineHeight: 1.4 },
  iconBadgeFrozen: { backgroundColor: "#F3F4F6", filter: "grayscale(1)" },
  orderListFrozen: { backgroundColor: "#F3F4F6", borderColor: "#E5E7EB" },
  header: { display: "flex", alignItems: "flex-start", gap: "9px" },
  iconBadge: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    backgroundColor: "#EEF2FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  headerText: { fontSize: "13.5px", fontWeight: 600, color: "#111827", lineHeight: 1.45, paddingTop: "3px" },
  blockedBanner: {
    fontSize: "12.5px",
    fontWeight: 500,
    color: "#92400E",
    lineHeight: 1.45,
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "10px",
    padding: "9px 11px",
  },
  orderList: {
    display: "flex",
    flexDirection: "column",
    border: "1px solid #EEF0F4",
    borderRadius: "10px",
    backgroundColor: "#FBFBFD",
    overflow: "hidden",
  },
  orderRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    padding: "9px 12px",
    cursor: "pointer",
  },
  orderRowDivider: { borderTop: "1px solid #F0F1F5" },
  orderRowBusy: { cursor: "default" },
  checkbox: {
    width: "15px",
    height: "15px",
    marginTop: "2px",
    flexShrink: 0,
    accentColor: "#4F46E5",
    cursor: "inherit",
  },
  orderTextCol: { flex: 1, display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  orderNameRow: { display: "flex", alignItems: "baseline", gap: "7px", flexWrap: "wrap", minWidth: 0 },
  orderName: { fontSize: "13px", fontWeight: 500, color: "#1F2937", lineHeight: 1.45, minWidth: 0 },
  orderDetail: { fontSize: "12px", fontWeight: 400, color: "#6B7280", lineHeight: 1.4 },
  orderMemory: { fontSize: "12px", fontWeight: 400, color: "#4F46E5", lineHeight: 1.4 },
  orderFuture: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#B45309",
    lineHeight: 1.4,
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",
  },
  dateInput: {
    fontSize: "12px",
    fontFamily: "inherit",
    color: "#1F2937",
    padding: "1px 4px",
    border: "1px solid #D6D9E0",
    borderRadius: "6px",
    backgroundColor: "#FFFFFF",
  },
  orderRationale: {
    fontSize: "11.5px",
    fontWeight: 400,
    color: "#9CA3AF",
    lineHeight: 1.4,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  orderNameUnchecked: { color: "#9CA3AF", fontWeight: 400 },
  // What the order becomes in the EHR: the resolved catalogue item, or the
  // inline pick when the EHR offered several. Reads as a continuation of the
  // row, not a second card.
  catalogLine: {
    display: "flex",
    alignItems: "baseline",
    gap: "5px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#4338CA",
    lineHeight: 1.4,
  },
  catalogArrow: { color: "#A5B4FC", flexShrink: 0 },
  // The diagnosis the order files under: a continuation of the row, one
  // step quieter than the catalogue line.
  dxLine: {
    display: "flex",
    alignItems: "baseline",
    gap: "5px",
    fontSize: "12px",
    fontWeight: 400,
    color: "#6B7280",
    lineHeight: 1.4,
  },
  // Wraps so the select keeps a readable width next to "Show more": in a
  // narrow column the two shared ~146px and the picked value was invisible.
  catalogPickRow: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    marginTop: "2px",
    minWidth: 0,
    flexWrap: "wrap",
  },
  catalogSelect: {
    flex: "1 1 160px",
    minWidth: 0,
    maxWidth: "100%",
    fontSize: "12px",
    fontWeight: 500,
    color: "#4338CA",
    padding: "3px 6px",
    borderRadius: "7px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "#C7D2FE",
    backgroundColor: "#FFFFFF",
    fontFamily: "inherit",
    cursor: "pointer",
  },
  catalogSelectEmpty: { color: "#B45309", borderColor: "#FDE68A", backgroundColor: "#FFFBEB" },
  catalogMiss: { fontSize: "11.5px", fontWeight: 400, color: "#B45309", lineHeight: 1.4 },
  duplicateBadge: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "3px 7px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    color: "#B45309",
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
  },
  // Coverage-aware card (POC). The strip sits under the header; each row
  // stacks its coverage badge and cost under the type badge; the detail line
  // is the row's last line, one step quieter than the diagnosis.
  coverageStrip: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: "7px 10px",
    borderRadius: "8px",
    backgroundColor: "#F3F4F8",
    border: "1px solid #E5E7EB",
  },
  coverageStripMain: { fontSize: "12px", fontWeight: 500, color: "#374151", lineHeight: 1.4 },
  coverageStripCaption: { fontSize: "11px", fontWeight: 400, color: "#9CA3AF", lineHeight: 1.4 },
  rowBadgeCol: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0 },
  coverageBadge: {
    fontSize: "10.5px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "3px 7px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    borderWidth: "1px",
    borderStyle: "solid",
  },
  coverageCost: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    fontSize: "12px",
    fontWeight: 600,
    color: "#1F2937",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  coverageCostDetail: { fontSize: "10.5px", fontWeight: 400, color: "#9CA3AF" },
  coverageDetail: { fontSize: "11.5px", fontWeight: 400, color: "#6B7280", lineHeight: 1.4 },
  coverageHint: { color: "#4338CA", fontWeight: 500 },
  coverageTotal: { fontSize: "12px", color: "#374151", lineHeight: 1.4 },
  typeBadge: {
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "4px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    marginTop: "1px",
  },
  stopsBox: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "10px 12px",
    borderRadius: "10px",
    border: "1px dashed #E5E7EB",
    backgroundColor: "#FCFCFD",
  },
  stopsTitle: { fontSize: "11.5px", fontWeight: 600, color: "#6B7280", lineHeight: 1.4 },
  stopRow: { display: "flex", alignItems: "flex-start", gap: "8px" },
  stopBadge: {
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: 1,
    padding: "4px 8px",
    borderRadius: "999px",
    whiteSpace: "nowrap",
    flexShrink: 0,
    color: "#B91C1C",
    backgroundColor: "#FEF2F2",
  },
  stopText: { fontSize: "12.5px", fontWeight: 400, color: "#4B5563", lineHeight: 1.45 },
  unparsedLine: { fontSize: "12px", fontWeight: 500, color: "#B45309", lineHeight: 1.4 },
  hint: { fontSize: "12px", color: "#9CA3AF", lineHeight: 1.4 },
  pickHint: { fontSize: "12px", color: "#B45309", fontWeight: 500, lineHeight: 1.4 },
  actions: { display: "flex", flexDirection: "column", gap: "8px" },
  secondaryRow: { display: "flex", gap: "8px", alignItems: "stretch" },
  primaryButton: {
    width: "100%",
    padding: "9px 14px",
    fontSize: "13px",
    fontWeight: 600,
    borderRadius: "10px",
    border: "1px solid #4F46E5",
    backgroundColor: "#4F46E5",
    color: "#FFFFFF",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  secondaryButton: {
    flex: 1,
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    border: "1px solid #D6D9E0",
    backgroundColor: "#FFFFFF",
    color: "#374151",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  ghostButton: {
    flex: "0 0 auto",
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "10px",
    border: "1px solid transparent",
    backgroundColor: "transparent",
    color: "#6B7280",
    cursor: "pointer",
    fontFamily: "inherit",
    lineHeight: 1.4,
    boxSizing: "border-box",
  },
  buttonDisabled: { opacity: 0.55, cursor: "not-allowed" },
};

export default OrderSuggestions;
